/**
 * AgentNotification - Toast-style notifications for AI pipeline events
 * Shows real-time agent activity: started, completed, needs review
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../../utils/api';
import {
  Brain,
  CheckCircle,
  AlertTriangle,
  X,
  Loader2,
  FileSearch,
  Scale,
  Shield,
  TrendingUp,
  Users,
  FileText,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface AgentEvent {
  id: string;
  agent_name: string;
  event_type: 'started' | 'completed' | 'needs_review' | 'error';
  message: string;
  timestamp: string;
  report_id?: string;
  auto_dismiss?: boolean;
}

interface AgentNotificationProps {
  maxNotifications?: number;
  position?: 'top-right' | 'bottom-right';
  autoDismissMs?: number;
}

const AGENT_ICONS: Record<string, React.ElementType> = {
  document: FileText,
  transaction: TrendingUp,
  anomaly: FileSearch,
  forensic: Shield,
  attorney: Scale,
  mediator: Users,
  report: FileText,
  default: Brain,
};

const EVENT_STYLES: Record<
  string,
  {
    boxStyle: React.CSSProperties;
    icon: React.ElementType;
    iconClassName: string;
    iconStyle: React.CSSProperties;
    textStyle: React.CSSProperties;
  }
> = {
  started: {
    boxStyle: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    icon: Loader2,
    iconClassName: 'animate-spin',
    iconStyle: { color: 'var(--color-needs-human-text)' },
    textStyle: { color: 'var(--color-needs-human-text)' },
  },
  completed: {
    boxStyle: { backgroundColor: 'transparent', borderColor: 'var(--color-verified)' },
    icon: CheckCircle,
    iconClassName: '',
    iconStyle: { color: 'var(--color-verified)' },
    textStyle: { color: 'var(--color-verified)' },
  },
  needs_review: {
    boxStyle: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    icon: AlertTriangle,
    iconClassName: '',
    iconStyle: { color: 'var(--color-brand)' },
    textStyle: { color: 'var(--color-needs-human-text)' },
  },
  error: {
    boxStyle: { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    icon: AlertTriangle,
    iconClassName: '',
    iconStyle: { color: 'var(--color-brand)' },
    textStyle: { color: 'var(--color-needs-human-text)' },
  },
};

export default function AgentNotification({
  maxNotifications = 5,
  position = 'bottom-right',
  autoDismissMs = 8000,
}: AgentNotificationProps) {
  const [notifications, setNotifications] = useState<AgentEvent[]>([]);

  // Connect to WebSocket for real-time events
  useEffect(() => {
    const wsUrl = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;

    let ws: WebSocket | null = null;
    let reconnectTimer: NodeJS.Timeout;
    // Set on unmount; checked after the async mint so a socket opened after
    // unmount can't become a zombie that reconnects/re-mints forever.
    let disposed = false;

    const connect = async () => {
      // AUTHZ (2026-10-03): the global pipeline WS requires a short-lived token
      // minted by an authenticated REST call (report_id='all', available to any
      // authenticated user). Mint first, then connect with ?token=. If we can't
      // mint (signed out / network), back off and retry rather than opening an
      // unauthenticated socket that would receive nothing.
      let wsToken = '';
      try {
        const authToken = await getAuthToken();
        const resp = await fetch(
          `${BACKEND_URL}/api/v1/ws/mint-token?report_id=all`,
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
        );
        if (resp.ok) {
          wsToken = (await resp.json())?.token || '';
        }
      } catch {
        // ignore — no live notifications without a token
      }
      if (disposed) return;
      if (!wsToken) {
        reconnectTimer = setTimeout(connect, 30000);
        return;
      }
      try {
        ws = new WebSocket(`${wsUrl}/api/v1/ws/pipeline?token=${encodeURIComponent(wsToken)}`);

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            // Parse pipeline events into notifications
            if (data.type === 'agent_update' || data.event_type) {
              const agentEvent: AgentEvent = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                agent_name: data.agent_name || data.agent || 'AI Agent',
                event_type: data.status === 'running' ? 'started'
                  : data.status === 'completed' ? 'completed'
                  : data.status === 'needs_review' ? 'needs_review'
                  : data.event_type || 'started',
                message: data.message || `${data.agent_name || 'Agent'} ${data.status || 'processing'}`,
                timestamp: new Date().toISOString(),
                report_id: data.report_id || data.claim_id,
                auto_dismiss: data.status !== 'needs_review',
              };
              addNotification(agentEvent);
            }
          } catch {
            // Ignore parse errors
          }
        };

        ws.onclose = () => {
          if (!disposed) reconnectTimer = setTimeout(connect, 5000);
        };

        ws.onerror = () => {
          ws?.close();
        };
      } catch {
        if (!disposed) reconnectTimer = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, []);

  const addNotification = useCallback(
    (event: AgentEvent) => {
      setNotifications((prev) => {
        const next = [event, ...prev].slice(0, maxNotifications);
        return next;
      });

      // Auto-dismiss informational events
      if (event.auto_dismiss !== false && event.event_type !== 'needs_review') {
        setTimeout(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== event.id));
        }, autoDismissMs);
      }
    },
    [maxNotifications, autoDismissMs]
  );

  const dismiss = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  if (notifications.length === 0) return null;

  const posClass = position === 'top-right' ? 'top-4 right-4' : 'bottom-4 right-4';

  return (
    <div className={`fixed ${posClass} z-50 flex flex-col gap-2 w-80 pointer-events-none`}>
      {notifications.map((n) => {
        const style = EVENT_STYLES[n.event_type] || EVENT_STYLES.started;
        const EventIcon = style.icon;
        const agentKey = Object.keys(AGENT_ICONS).find((k) => n.agent_name.toLowerCase().includes(k)) || 'default';
        const AgentIcon = AGENT_ICONS[agentKey];

        return (
          <div
            key={n.id}
            className="pointer-events-auto border rounded-xl p-3 shadow-lg transition-all duration-300 animate-in slide-in-from-right"
            style={style.boxStyle}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <EventIcon className={`w-5 h-5 ${style.iconClassName}`} style={style.iconStyle} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <AgentIcon className="w-3.5 h-3.5" style={{ color: 'var(--color-text-muted)' }} />
                  <p className="text-sm font-medium" style={style.textStyle}>{n.agent_name}</p>
                </div>
                <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-secondary)' }}>{n.message}</p>
              </div>
              <button
                onClick={() => dismiss(n.id)}
                className="flex-shrink-0"
                style={{ color: 'var(--color-text-dim)' }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
